import React, { useState, useEffect, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { useIsMobile, merge } from "../hooks/useIsMobile";
import { COLORS, SERIF, SANS, MONO, pageStyles } from "../components/dispute/theme";

// The public index of decided disputes: every case whose filer (or the
// platform) published it, once the panel has ruled. Backed by GET /api/share,
// which is unauthenticated and returns only what a row needs — title, a
// 200-character excerpt of the filer's statement, and the uuid the read-only
// dispute page is keyed by. Anyone flagged private simply never appears.
//
// The page redirects to /home when the index comes back empty — an index with
// nothing in it isn't a page, and the nav/footer links hide themselves to match
// (see hooks/useHasPublicDisputes.js).

const API_BASE = process.env.REACT_APP_API_URL || "";

const PAGE_SIZE = 25;

export default function PublicDisputes() {
  const isMobile = useIsMobile();
  const [disputes, setDisputes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // `offset` is passed in rather than read from state so "Load more" can't
  // request the same page twice while a fetch is already in flight.
  const load = useCallback(async (offset = 0) => {
    if (offset === 0) setLoading(true);
    else setLoadingMore(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/share?limit=${PAGE_SIZE}&offset=${offset}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setTotal(data.total || 0);
      setDisputes((list) => (offset === 0 ? data.disputes || [] : [...list, ...(data.disputes || [])]));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { load(0); }, [load]);

  // Nothing published yet ⇒ the page doesn't exist. The header/footer links are
  // hidden in that case (useHasPublicDisputes), so this only catches someone
  // typing the URL, or the last public case being unpublished mid-session.
  // An error is *not* an empty index — that keeps its own retry state below.
  if (!loading && !error && disputes.length === 0) {
    return <Navigate to="/home" replace />;
  }

  return (
    <div style={pageStyles.root}>
      <SiteHeader />

      <main style={merge(styles.main, styles.mainMobile, isMobile)}>
        <header style={styles.pageHeader}>
          <span style={styles.eyebrow}>Public record</span>
          <h1 style={styles.title}>Decided disputes</h1>
          <p style={styles.subtitle}>
            Cases the parties chose to publish once the panel had ruled. Names, evidence
            files and any money involved stay private — only the case and its decision are shown.
          </p>
        </header>

        {loading && <div style={styles.state}>Loading…</div>}

        {!loading && error && (
          <div style={styles.state}>
            Couldn’t load the public disputes ({error}).{" "}
            <button type="button" onClick={() => load(0)} style={styles.retry}>Try again</button>
          </div>
        )}

        {!loading && !error && disputes.length > 0 && (
          <>
            <ul style={styles.list}>
              {disputes.map((d) => (
                <li key={d.uuid} style={merge(styles.row, styles.rowMobile, isMobile)}>
                  <div style={styles.rowBody}>
                    <h2 style={styles.rowTitle}>{d.subject || "Untitled dispute"}</h2>
                    {d.excerpt && <p style={styles.rowExcerpt}>{d.excerpt}</p>}
                  </div>
                  <Link to={`/dispute/${d.uuid}`} style={styles.viewLink}>View dispute →</Link>
                </li>
              ))}
            </ul>

            {disputes.length < total && (
              <div style={styles.moreWrap}>
                <button
                  type="button"
                  onClick={() => load(disputes.length)}
                  disabled={loadingMore}
                  style={styles.moreBtn}
                >
                  {loadingMore ? "Loading…" : `Load more (${total - disputes.length} left)`}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}

const styles = {
  main: { flex: 1, width: "100%", maxWidth: 860, margin: "0 auto", padding: "48px 24px 72px" },
  mainMobile: { padding: "32px 20px 56px" },

  pageHeader: { marginBottom: 32 },
  eyebrow: {
    fontFamily: MONO, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase",
    color: COLORS.inkMuted, display: "block", marginBottom: 10,
  },
  title: { ...pageStyles.pageTitle, fontFamily: SERIF, marginBottom: 10 },
  subtitle: { ...pageStyles.pageSubtitle, margin: 0, maxWidth: 620 },

  list: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 12 },
  row: {
    display: "flex", alignItems: "center", gap: 24,
    background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14,
    padding: "20px 24px",
  },
  rowMobile: { flexDirection: "column", alignItems: "flex-start", gap: 14, padding: "18px 20px" },
  rowBody: { flex: 1, minWidth: 0 },
  rowTitle: {
    fontFamily: SERIF, fontSize: 19, fontWeight: 600, color: COLORS.ink,
    margin: 0, letterSpacing: "-0.2px",
  },
  rowExcerpt: {
    fontFamily: SANS, fontSize: 14, lineHeight: 1.6, color: COLORS.inkSoft,
    margin: "8px 0 0 0",
  },
  viewLink: {
    flexShrink: 0, fontFamily: SANS, fontSize: 13.5, fontWeight: 600,
    color: COLORS.dark, textDecoration: "none",
    border: `1px solid ${COLORS.borderStrong}`, borderRadius: 8, padding: "10px 16px",
    whiteSpace: "nowrap",
  },

  state: {
    fontFamily: SANS, fontSize: 14, color: COLORS.inkMuted,
    background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14,
    padding: "28px 24px", textAlign: "center",
  },
  retry: {
    background: "none", border: "none", padding: 0, cursor: "pointer",
    fontFamily: SANS, fontSize: 14, color: COLORS.ink, textDecoration: "underline",
  },

  moreWrap: { display: "flex", justifyContent: "center", marginTop: 24 },
  moreBtn: {
    fontFamily: SANS, fontSize: 13.5, fontWeight: 600, color: COLORS.ink,
    background: COLORS.card, border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: 8, padding: "11px 20px", cursor: "pointer",
  },
};
